import React, { Fragment, useContext, useEffect, useRef } from 'react';
import { Observer } from 'mobx-react-lite';
import FadeIn from 'react-fade-in';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import BookingTypeUpdateStore from '../../../../stores/BookingTypeUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';


import './NewBookingType.scss';

moment.locale('en');
momentLocalizer();

function NewBookingType(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const bookingTypeUpdate = useContext(BookingTypeUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (bookingTypeUpdate.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            bookingTypeUpdate.new()
                .then(() => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: true, data: { id: bookingTypeUpdate.id } });
                        }
                    }
                })
        }
    }

    const handleCodeChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.code = value ? value.toUpperCase() : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleNameChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.name = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleSubtitleChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.subtitle = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleDescriptionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.description = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleIsRequestOnlyChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.isRequestOnly = (value === 'Request');
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleIsHiddenChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.isHidden = (value === 'Hidden');
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleAnyoneAvailableEnabledChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.anyoneAvailableEnabled = (value === 'Anyone');
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleAnyoneAvailableTextChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.anyoneAvailableLabel = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleAnyoneAvailablePriorityChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.anyoneAvailablePriority = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleSexQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.sexQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleGenderQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.genderQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handlePronounQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.pronounQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleDateOfBirthQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.dateOfBirthQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleHealthCardNumberQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.healthCardNumberQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handlePhoneQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.phoneQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleIsNewOrReturnQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.isNewOrReturnQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleMessageQuestionChange = (event) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.messageQuestion = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleCustomQuestionChange = (event, index) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.customQuestions[index].question = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleCustomQuestionTypeChange = (event, index) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.customQuestions[index].type = value ? value : null;
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleCustomQuestionIsRequiredChange = (event, index) => {
        const { value } = event.target;
        bookingTypeUpdate.newBookingType.customQuestions[index].isRequired = (value === 'Required');
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleCustomQuestionAdd = (event) => {
        if (!bookingTypeUpdate.newBookingType.customQuestions || !Array.isArray(bookingTypeUpdate.newBookingType.customQuestions)) {
            bookingTypeUpdate.newBookingType.customQuestions = [];
        }

        if (bookingTypeUpdate.newBookingType.customQuestions.length < 3) {
            bookingTypeUpdate.newBookingType.customQuestions.push({
                question: null,
                type: 'SingleLine',
                isRequired: false,
            });
            bookingTypeUpdate.hasUnsavedChanges = true;
        }
    }

    const handleCustomQuestionRemove = (event, index) => {
        if (bookingTypeUpdate.newBookingType.customQuestions && bookingTypeUpdate.newBookingType.customQuestions.length > 0) {
            bookingTypeUpdate.newBookingType.customQuestions.splice(index, 1);
            bookingTypeUpdate.hasUnsavedChanges = true;
        }
    }

    return <>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <>
                    {
                        (props.drawer === quickDrawer.drawerOpened) ?
                            <GlobalHotKeys
                                keyMap={{
                                    close: ['esc'],
                                }}
                                handlers={{
                                    close: event => {
                                        handleCancel(event)
                                    },
                                }}
                                allowChanges={true}
                            /> : null
                    }
                    <fieldset disabled={bookingTypeUpdate.isSaving}>
                        <div className='quick-drawer'>
                            <QuickDrawerHeader
                                drawer={props.drawer}
                                icon={oh.getIcon('appointment', 'update')}
                                action='New'
                                category='Online Booking Type'
                                className='appointments'
                                onCancel={handleCancel}
                            />
                            <div className='quick-drawer-body'>
                                {
                                    bookingTypeUpdate.isReady ?
                                        <FadeIn>
                                            <div className='new-online-booking-type body-content'>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>General Information</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Code</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control text-uppercase'
                                                                maxLength={15}
                                                                value={(bookingTypeUpdate.newBookingType.code ? bookingTypeUpdate.newBookingType.code : '')}
                                                                onChange={handleCodeChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Title</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                maxLength={150}
                                                                value={(bookingTypeUpdate.newBookingType.name ? bookingTypeUpdate.newBookingType.name : '')}
                                                                onChange={handleNameChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>Subtitle</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                maxLength={75}
                                                                value={(bookingTypeUpdate.newBookingType.subtitle ? bookingTypeUpdate.newBookingType.subtitle : '')}
                                                                onChange={handleSubtitleChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Description</small></label>
                                                            <textarea
                                                                className='form-control'
                                                                rows={5}
                                                                maxLength={3000}
                                                                value={(bookingTypeUpdate.newBookingType.description ? bookingTypeUpdate.newBookingType.description : '')}
                                                                onChange={handleDescriptionChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <hr />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>Booking Preference</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Mode</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(!!bookingTypeUpdate.newBookingType.isRequestOnly ? 'Request' : 'TimeSlot')}
                                                                onChange={handleIsRequestOnlyChange}
                                                            >
                                                                <option value='TimeSlot'>Timeslots</option>
                                                                <option value='Request'>Request Only</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Visibility</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(!!bookingTypeUpdate.newBookingType.isHidden ? 'Hidden' : 'Public')}
                                                                onChange={handleIsHiddenChange}
                                                            >
                                                                <option value='Public'>Online Booking Page</option>
                                                                <option value='Hidden'>Direct Link Only</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    !bookingTypeUpdate.newBookingType.isRequestOnly ?
                                                        <section className='m-0 mb-4'>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <label><small>Selection</small></label>
                                                                    <select
                                                                        className='custom-select form-control'
                                                                        value={(!!bookingTypeUpdate.newBookingType.anyoneAvailableEnabled ? 'Anyone' : 'Specific')}
                                                                        onChange={handleAnyoneAvailableEnabledChange}
                                                                    >
                                                                        <option value='Specific'>Specific doctor / staff</option>
                                                                        <option value='Anyone'>Anyone available</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className={'row' + (!!bookingTypeUpdate.newBookingType.anyoneAvailableEnabled ? ' mt-4' : ' d-none')}>
                                                                <div className='col-6'>
                                                                    <label><small>Anyone available label</small></label>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        value={(bookingTypeUpdate.newBookingType.anyoneAvailableLabel ? bookingTypeUpdate.newBookingType.anyoneAvailableLabel : '')}
                                                                        onChange={handleAnyoneAvailableTextChange}
                                                                    />
                                                                </div>
                                                                <div className='col-6'>
                                                                    <label><small>Anyone available priority</small></label>                                                                    
                                                                    <select
                                                                        className='custom-select form-control'
                                                                        value={bookingTypeUpdate.newBookingType.anyoneAvailablePriority}
                                                                        onChange={handleAnyoneAvailablePriorityChange}
                                                                    >
                                                                        <option value='DisplayOrder'>Order of display</option>
                                                                        <option value='Availabilities'>Most available</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </section> : null
                                                }
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <hr />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>Booking Questions</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>First Name</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Last Name</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-4'>
                                                            <label><small>Sex</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.sexQuestion ? bookingTypeUpdate.newBookingType.sexQuestion : '')}
                                                                onChange={handleSexQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-4'>
                                                            <label><small>Gender</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.genderQuestion ? bookingTypeUpdate.newBookingType.genderQuestion : '')}
                                                                onChange={handleGenderQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-4'>
                                                            <label><small>Pronoun</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.pronounQuestion ? bookingTypeUpdate.newBookingType.pronounQuestion : '')}
                                                                onChange={handlePronounQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Date of Birth</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.dateOfBirthQuestion ? bookingTypeUpdate.newBookingType.dateOfBirthQuestion : '')}
                                                                onChange={handleDateOfBirthQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Health Card Number</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.healthCardNumberQuestion ? bookingTypeUpdate.newBookingType.healthCardNumberQuestion : '')}
                                                                onChange={handleHealthCardNumberQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Email</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Phone</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.phoneQuestion ? bookingTypeUpdate.newBookingType.phoneQuestion : '')}
                                                                onChange={handlePhoneQuestionChange}
                                                            >
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>New or Return Patient</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.isNewOrReturnQuestion ? bookingTypeUpdate.newBookingType.isNewOrReturnQuestion : '')}
                                                                onChange={handleIsNewOrReturnQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>Message</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingTypeUpdate.newBookingType.messageQuestion ? bookingTypeUpdate.newBookingType.messageQuestion : '')}
                                                                onChange={handleMessageQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    bookingTypeUpdate.newBookingType.customQuestions && bookingTypeUpdate.newBookingType.customQuestions.length > 0 ?
                                                        <>
                                                            {
                                                                bookingTypeUpdate.newBookingType.customQuestions.map((q, qi) => {
                                                                    return <Fragment
                                                                        key={`new_booking_type_${qi}`}
                                                                    >
                                                                        <section className='m-0 mb-2'>
                                                                            <div className='row'>
                                                                                <div className='col-12 validate validate-required'>
                                                                                    <div className='d-flex align-items-center mb-1'>
                                                                                        <label className='flex-1 m-0 required'><small>Custom Question #{(qi + 1)}</small></label>
                                                                                        <button
                                                                                            type='button'
                                                                                            className='btn btn-icon line-height-1 mr-n2'
                                                                                            onClick={(e) => { handleCustomQuestionRemove(e, qi) }}
                                                                                        >
                                                                                            <i className='fal fa-times fs-sm text-danger'></i>
                                                                                        </button>
                                                                                    </div>
                                                                                    <input
                                                                                        type='text'
                                                                                        className='form-control'
                                                                                        maxLength={255}
                                                                                        value={(q.question ? q.question : '')}
                                                                                        onChange={(e) => { handleCustomQuestionChange(e, qi) }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </section>
                                                                        <section className='m-0 mb-4'>
                                                                            <div className='row'>
                                                                                <div className='col-6'>
                                                                                    <select
                                                                                        className='custom-select form-control'
                                                                                        value={(q.type ? q.type : '')}
                                                                                        onChange={(e) => { handleCustomQuestionTypeChange(e, qi) }}
                                                                                    >
                                                                                        <option value='SingleLine'>Single-line Textbox</option>
                                                                                        <option value='MultiLine'>Multi-line Textbox</option>
                                                                                    </select>
                                                                                </div>
                                                                                <div className='col-6'>
                                                                                    <select
                                                                                        className='custom-select form-control'
                                                                                        value={(!!q.isRequired ? 'required' : '')}
                                                                                        onChange={(e) => { handleCustomQuestionIsRequiredChange(e, qi) }}
                                                                                    >
                                                                                        <option value=''>Optional</option>
                                                                                        <option value='Required'>Required</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        </section>
                                                                    </Fragment>
                                                                })
                                                            }
                                                        </> : null
                                                }
                                                <>
                                                    {
                                                        bookingTypeUpdate.newBookingType.customQuestions && bookingTypeUpdate.newBookingType.customQuestions.length < 3 ?
                                                            <section className='m-0 mb-4'>
                                                                <div className='row'>
                                                                    <div className='col-12'>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-link p-0'
                                                                            onClick={handleCustomQuestionAdd}
                                                                        >
                                                                            Add custom question
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </section> : null
                                                    }
                                                </>
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </div>
                            <Observer>{() =>
                                bookingTypeUpdate.isReady ?
                                    <div className='quick-drawer-action'>
                                        <div className='row'>
                                            <div className='col-12'>
                                                <div className='float-right'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-link btn-cancel'
                                                        onClick={handleCancel}
                                                    >Cancel</button>
                                                    <button
                                                        type='submit'
                                                        className='btn btn-success'
                                                    >Save</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div> : null
                            }</Observer>
                        </div>
                    </fieldset>
                </>
            }</Observer>
        </form>
    </>
}

export default NewBookingType;